"use client";
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import CustomImage from "../../atoms/customImage";
import { shallowEqual, useSelector } from "react-redux";

/**
 *
 * @returns The special effects for occasions with data coming web control features
 */
const OccasionSpecialEffects = ({ isMobile = false }) => {
  const [cursorHearts, setCursorHearts] = useState([]);
  const [heartIdCounter, setHeartIdCounter] = useState(0);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });
  const [showFallingHearts, setShowFallingHearts] = useState(true);
  const [fadeFallingHearts, setFadeFallingHearts] = useState(false);
  const { icon, isTailEnabled } = useSelector(
    (state) => ({
      icon: state?.webFeatureControls?.data?.occasionSpecialEffect?.icon,
      isTailEnabled: state?.webFeatureControls?.data?.occasionSpecialEffect?.isTailEnabled,
    }),
    shallowEqual,
  );

  const hearts = useMemo(
    () =>
      Array.from({ length: 40 }, (_, i) => ({
        id: i,
        x: Math.random() * 100, // Random horizontal position (%)
        size: Math.random() * 20 + 12, // Size between 12px and 32px
        duration: Math.random() * 15 + 10, //  Fall duration
        delay: Math.random() * 20, // Random delay up to 20s
        opacity: Math.random() * 0.15 + 0.05, // Very subtle opacity between 0.05-0.2
      })),
    [],
  );

  // Fade out and then remove falling hearts
  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setFadeFallingHearts(true); // Start fade
    }, 30000); // 10 seconds

    const removeTimer = setTimeout(() => {
      setShowFallingHearts(false); // Remove from DOM after fade completes
    }, 32000); // 12 seconds (10s + 2s fade duration)

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeTimer);
    };
  }, []);
  /**  */
  const renderHearts = (size) => {
    return <CustomImage src={icon} width={size} height={size} />;
  };

  useEffect(() => {
    /** */
    const handleMouseMove = (e) => {
      // Calculate distance from last position
      const distance = Math.sqrt(Math.pow(e.clientX - lastMousePos.x, 2) + Math.pow(e.clientY - lastMousePos.y, 2));

      // Only create a heart if the cursor has moved a certain distance
      if (distance > 15) {
        const newHeart = {
          id: Math.random() * heartIdCounter,
          x: e.clientX,
          y: e.clientY,
          size: 12, // Slightly varied sizes for more character
        };

        setHeartIdCounter((prev) => prev + 1);
        setCursorHearts((prev) => [...prev, newHeart]);
        setLastMousePos({ x: e.clientX, y: e.clientY });

        // Remove the heart after animation completes
        setTimeout(() => {
          setCursorHearts((prev) => prev.filter((heart) => heart.id !== newHeart.id));
        }, 1000);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [heartIdCounter, lastMousePos]);

  if (!icon) return null;
  return (
    <div
      className="pointer-events-none fixed inset-0 z-[100] overflow-hidden"
      style={{
        pointerEvents: "none",
        touchAction: "none",
        userSelect: "none",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        WebkitTapHighlightColor: "transparent",
      }}
    >
      {showFallingHearts &&
        hearts.map((heart) => (
          <div
            key={heart.id}
            className="duration-2000 pointer-events-none absolute transition-opacity"
            style={{
              pointerEvents: "none",
              touchAction: "none",
              left: `${heart.x}%`,
              top: "-50px",
              opacity: fadeFallingHearts ? 0 : heart.opacity,
              animation: `fall-${heart.id} ${heart.duration}s linear ${heart.delay}s infinite`,
            }}
          >
            <style jsx={true}>{`
              @keyframes fall-${heart.id} {
                0% {
                  transform: translateY(0) rotate(0deg) translateX(0);
                }
                100% {
                  transform: translateY(110vh) rotate(360deg) translateX(${Math.sin(heart.id) * 30}px);
                }
              }
            `}</style>
            {renderHearts(heart.size)}
          </div>
        ))}
      {!isMobile &&
        isTailEnabled &&
        cursorHearts.map((heart) => (
          <div
            key={heart.id}
            className="pointer-events-none absolute animate-cursorHeart"
            style={{
              left: heart.x,
              top: heart.y,
            }}
          >
            {renderHearts(heart.size)}
          </div>
        ))}
    </div>
  );
};

OccasionSpecialEffects.propTypes = {
  isMobile: PropTypes.bool,
};

export default OccasionSpecialEffects;
